import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['templateSelect'];
  static values = {
    selectTemplate: String,
    noTemplate: String
  };

  connect() {
    this.isDirty = false;
    this.ignoreChanges = false;
    this.loadAvailableTemplates();

    const sectorSelect = document.querySelector('[data-profiles-form-target="sectorId"]');

    if (sectorSelect) {
      sectorSelect.addEventListener('change', () => {
        this.loadAvailableTemplates();
        this.templateSelectTarget.value = '';
      });
    }

    setTimeout(() => {
      this.setupDirtyTracking();
    }, 0);
  }

  setupDirtyTracking() {
    this.handlers = {
      input: (event) => {
        if (event.target.matches('[data-sub-hour-bank-form-target="hours"]')) {
          this.markAsDirty();
        }
      },
      change: (event) => {
        if (event.target.matches('[data-sub-hour-bank-form-target="name"]')) {
          this.markAsDirty();
        }
      },
      click: (event) => {
        if (event.target.closest('[data-action*="sub-hour-bank-form#addLine"]')) {
          this.markAsDirty();
        }
        // Track delete button clicks
        if (event.target.closest('[data-action*="sub-hour-bank-form#deleteRow"]')) {
          this.markAsDirty();
        }
      }
    };

    document.addEventListener('input', this.handlers.input);
    document.addEventListener('change', this.handlers.change);
    document.addEventListener('click', this.handlers.click);

    const profilesFormController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="profiles-form"]'),
      'profiles-form'
    );

    if (profilesFormController) {
      this.observers = [];
      profilesFormController.subHourBankTargets.forEach((subHourBankFormTarget) => {
        const subHourBankFormController = this.application.getControllerForElementAndIdentifier(
          subHourBankFormTarget,
          'sub-hour-bank-form'
        );

        if (subHourBankFormController && subHourBankFormController.formTarget) {
          const observer = new MutationObserver(() => {
            this.markAsDirty();
          });

          observer.observe(subHourBankFormController.formTarget, {
            childList: true,
            subtree: false
          });

          this.observers.push(observer);
        }
      });
    }
  }

  disconnect() {
    if (this.handlers) {
      document.removeEventListener('input', this.handlers.input);
      document.removeEventListener('change', this.handlers.change);
      document.removeEventListener('click', this.handlers.click);
    }

    if (this.observers) {
      this.observers.forEach((observer) => observer.disconnect());
    }
  }

  markAsDirty() {
    if (!this.ignoreChanges) {
      this.isDirty = true;
    }
  }

  markAsPristine() {
    this.isDirty = false;
  }

  disconnectObservers() {
    if (this.observers) {
      this.observers.forEach((observer) => observer.disconnect());
    }
  }

  reconnectObservers() {
    this.disconnectObservers();

    const profilesFormController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="profiles-form"]'),
      'profiles-form'
    );

    if (profilesFormController) {
      this.observers = [];
      profilesFormController.subHourBankTargets.forEach((subHourBankFormTarget) => {
        const subHourBankFormController = this.application.getControllerForElementAndIdentifier(
          subHourBankFormTarget,
          'sub-hour-bank-form'
        );

        if (subHourBankFormController && subHourBankFormController.formTarget) {
          const observer = new MutationObserver(() => {
            this.markAsDirty();
          });

          observer.observe(subHourBankFormController.formTarget, {
            childList: true,
            subtree: false
          });

          this.observers.push(observer);
        }
      });
    }
  }

  async loadAvailableTemplates() {
    const sectorSelect = document.querySelector('[data-profiles-form-target="sectorId"]');

    if (!sectorSelect) {
      return;
    }

    const sectorId = sectorSelect.value;

    if (!sectorId) {
      this.templateSelectTarget.innerHTML = `<option value="">${this.selectTemplateValue}</option>`;
      this.templateSelectTarget.disabled = true;
      this.element.style.display = '';

      return;
    }

    try {
      const response = await fetch(`/profiles/templates?sector_id=${sectorId}`);
      const data = await response.json();

      this.templateSelectTarget.innerHTML = `<option value="">${this.noTemplateValue}</option>`;

      if (data.templates && data.templates.length > 0) {
        data.templates.forEach((template) => {
          const option = document.createElement('option');

          option.value = JSON.stringify(template);
          option.textContent = template.name;
          this.templateSelectTarget.appendChild(option);
        });
        this.templateSelectTarget.disabled = false;
        this.element.style.display = '';
      } else {
        this.templateSelectTarget.disabled = true;
        this.element.style.display = 'none';
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      this.templateSelectTarget.disabled = true;
    }
  }

  loadTemplate(event) {
    const templateData = event.target.value;

    if (this.isDirty) {
      const previousValue = this.lastSelectedTemplate || '';

      event.target.value = previousValue;

      const confirmEvent = new CustomEvent('template-change-confirmation', {
        detail: {
          onConfirm: () => {
            this.lastSelectedTemplate = templateData;
            event.target.value = templateData;
            this.applyTemplate(templateData);
          }
        }
      });

      document.dispatchEvent(confirmEvent);

      return;
    }

    this.lastSelectedTemplate = templateData;
    this.applyTemplate(templateData);
  }

  applyTemplate(templateData) {
    this.disconnectObservers();

    this.ignoreChanges = true;

    if (!templateData) {
      this.clearAllSubHourBanks();
      this.markAsPristine();
      this.ignoreChanges = false;
      this.reconnectObservers();

      return;
    }

    try {
      const template = JSON.parse(templateData);

      this.populateSubHourBanks(template.sub_hour_banks);
      this.markAsPristine();
    } finally {
      this.ignoreChanges = false;
      this.reconnectObservers();
    }
  }

  clearAllSubHourBanks() {
    const profilesFormController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="profiles-form"]'),
      'profiles-form'
    );

    if (!profilesFormController) {
      return;
    }

    profilesFormController.subHourBankTargets.forEach((subHourBankFormTarget) => {
      const subHourBankFormController = this.application.getControllerForElementAndIdentifier(
        subHourBankFormTarget,
        'sub-hour-bank-form'
      );

      if (subHourBankFormController) {
        subHourBankFormController.clearAll();
      }
    });
  }

  populateSubHourBanks(subHourBanks) {
    const profilesFormController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="profiles-form"]'),
      'profiles-form'
    );

    if (!profilesFormController) {
      return;
    }

    const banksByCategory = {};

    subHourBanks.forEach((bank) => {
      if (!banksByCategory[bank.category_id]) {
        banksByCategory[bank.category_id] = [];
      }
      banksByCategory[bank.category_id].push(bank);
    });

    profilesFormController.subHourBankTargets.forEach((subHourBankFormTarget) => {
      const categoryId = subHourBankFormTarget.getAttribute('data-sub-hour-bank-form-category-id-value');
      const banks = banksByCategory[categoryId] || [];

      const subHourBankFormController = this.application.getControllerForElementAndIdentifier(
        subHourBankFormTarget,
        'sub-hour-bank-form'
      );

      if (subHourBankFormController) {
        subHourBankFormController.clearAll();

        banks.forEach((bank) => {
          subHourBankFormController.addLineWithData({
            name: bank.name,
            hours: 0,
            order: bank.order
          });
        });
      }
    });
  }
}
